import { useState } from "react";
import moment from "moment-timezone";
import { Button } from "@/components/ui/button";
import { ChevronDown, ChevronLeft, ChevronRight, ChevronUp, SlidersHorizontal } from "lucide-react";
import {
  useListAgtechTicketsExpanded,
  type AgtechTicketsExpandedFilters,
  type ExpandedTransformedTicket,
} from "@/lib/api/agtech/ticketsExpanded";
import { useListAgtechCommodities, type AgtechCommodity } from "@/lib/api/agtech/lookup";
import { useListLocations, type Location } from "@/lib/api/generalContract";
import { useListCustomers } from "@/lib/api/customer";
import { type Customer } from "@/lib/api/reports";
import { TicketFilterModal } from "./TicketFilterModal";
import { ContractTicketsModal } from "./ContractTicketsModal";
import { EntryBlock } from "./TicketComponents";
import {
  DEFAULT_PAGE_SIZE,
  DEFAULT_FILTER_VALUES,
  type ApiFilterValues,
} from "./types";

// ── Summary helpers ────────────────────────────────────────────────

function getTicketSummary(ticket: ExpandedTransformedTicket) {
  let settlementCount = 0;
  let advanceCount = 0;
  let customerName: string | null = null;
  let commodityName: string | null = null;
  let locationName: string | null = null;
  let shipDate: string | null = null;

  for (const entry of ticket.entries) {
    settlementCount += entry.settlements.length;
    advanceCount += entry.advances.length;
    if (!customerName && entry.Customer?.long_name) customerName = entry.Customer.long_name;
    if (!commodityName && entry.commodity_display_name) commodityName = entry.commodity_display_name;
    if (!locationName && entry.Location?.long_name) locationName = entry.Location.long_name;
    if (!shipDate && entry.ship_date) shipDate = entry.ship_date;
  }

  return {
    entryCount: ticket.entries.length,
    settlementCount,
    advanceCount,
    customerName,
    commodityName,
    locationName,
    shipDate,
  };
}

// ── Ticket Card ────────────────────────────────────────────────────

function TicketCard({
  ticket,
  isExpanded,
  onToggle,
  onContractClick,
}: {
  ticket: ExpandedTransformedTicket;
  isExpanded: boolean;
  onToggle: () => void;
  onContractClick: (item: { contract_nbr: number; contract_id: string }) => void;
}) {
  const summary = getTicketSummary(ticket);

  const counts: string[] = [];
  if (summary.entryCount > 0) counts.push(`${summary.entryCount} entr${summary.entryCount === 1 ? "y" : "ies"}`);
  if (summary.settlementCount > 0) counts.push(`${summary.settlementCount} settlement${summary.settlementCount === 1 ? "" : "s"}`);
  if (summary.advanceCount > 0) counts.push(`${summary.advanceCount} advance${summary.advanceCount === 1 ? "" : "s"}`);

  return (
    <div className="rounded-lg border bg-card text-card-foreground shadow-sm">
      {/* Collapsed header — always visible */}
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center gap-4 px-4 py-3 text-left hover:bg-muted/50 transition-colors rounded-lg"
      >
        {/* Chevron */}
        {isExpanded ? (
          <ChevronUp className="h-4 w-4 shrink-0 text-muted-foreground" />
        ) : (
          <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />
        )}

        {/* Ticket # */}
        <span className="text-sm font-semibold whitespace-nowrap">Ticket No: {ticket.scale_ticket_id}</span>

        {/* Summary fields */}
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 flex-1 min-w-0">
          {summary.locationName && (
            <span className="text-xs text-muted-foreground truncate max-w-[150px]">{summary.locationName}</span>
          )}
          {summary.commodityName && (
            <span className="text-xs text-muted-foreground truncate max-w-[150px]">{summary.commodityName}</span>
          )}
          {summary.customerName && (
            <span className="text-xs truncate max-w-[200px] font-bold">{summary.customerName}</span>
          )}
          {summary.shipDate && (
            <span className="text-xs text-muted-foreground whitespace-nowrap">
                                                                                Ship: {moment.tz(summary.shipDate, "UTC").format("MM/DD/YYYY")}
            </span>
          )}
        </div>

        {/* Counts badge */}
        {counts.length > 0 && (
          <span className="text-[10px] text-muted-foreground whitespace-nowrap shrink-0">
            {counts.join(" · ")}
          </span>
        )}
      </button>

      {/* Expanded detail */}
      {isExpanded && (
        <div className="px-4 pb-4 space-y-2">
          {ticket.entries.map((entry) => (
            <EntryBlock key={entry.quantity_entry_nbr} entry={entry} onContractClick={onContractClick} />
          ))}
        </div>
      )}
    </div>
  );
}

// ── Page ───────────────────────────────────────────────────────────

export function AgtechTicketsPage() {
  // ── Applied filter state (drives the API) ────────
  const [appliedFilters, setAppliedFilters] = useState<ApiFilterValues>(DEFAULT_FILTER_VALUES);

  // ── Filter modal state ───────────────────────────
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [draftFilters, setDraftFilters] = useState<ApiFilterValues>(DEFAULT_FILTER_VALUES);

  // ── Pagination state ─────────────────────────────
  const [page, setPage] = useState(1);
  const [pageSize] = useState(DEFAULT_PAGE_SIZE);

  // ── Card expand state ────────────────────────────
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());

  // ── Contract modal state ─────────────────────────
  const [selectedContractInfo, setSelectedContractInfo] = useState<{ contract_nbr: number; contract_id: string; settlement_nbr?: number; advance_nbr?: number } | null>(null);

  // ── Data for comboboxes ──────────────────────────
  const locationsResult = useListLocations();
  const customersResult = useListCustomers({ minimal: true });
  const commoditiesResult = useListAgtechCommodities();

  const locations: Location[] = locationsResult?.data?.data || [];

  const locationOptions = locations?.map((loc) => ({
    value: loc.external_id,
    label: loc.long_name,
  })) ?? [];

  const customers: Customer[] = customersResult?.data?.data || [];
  const customerOptions = customers?.map((cust) => ({
    value: cust.id,
    label: cust.long_name,
  })) ?? [];

  const shipCustomerOptions = customers?.map((cust) => ({
    value: cust.client_id,
    label: cust.long_name,
  })) ?? [];

  const commodities: AgtechCommodity[] = commoditiesResult?.data?.data || [];
  const commodityOptions = commodities.map((c) => ({
    value: c.commodity_id,
    label: c.name,
  }));

  // ── Build filters object ─────────────────────────
  const filters: AgtechTicketsExpandedFilters = {
    page,
    page_size: pageSize,
    scale_ticket_inbound_outbound: "I",
    ...(appliedFilters.scaleTicketId ? { scale_ticket_id: appliedFilters.scaleTicketId } : {}),
    ...(appliedFilters.shipDate ? { ship_date: appliedFilters.shipDate } : {}),
    ...(appliedFilters.plcId ? { plc_id: appliedFilters.plcId } : {}),
    ...(appliedFilters.commodityId ? { commodity_id: appliedFilters.commodityId } : {}),
    ...(appliedFilters.customerIds.length > 0 ? { customer_ids: appliedFilters.customerIds.join(",") } : {}),
    ...(appliedFilters.shipFrom ? { ship_from: appliedFilters.shipFrom } : {}),
    ...(appliedFilters.shipTo ? { ship_to: appliedFilters.shipTo } : {}),
  };

  // ── Data fetching ────────────────────────────────
  const ticketsResult = useListAgtechTicketsExpanded(filters);

  const isSuccess = !!ticketsResult?.data?.success;
  const tickets: ExpandedTransformedTicket[] = isSuccess && ticketsResult.data ? ticketsResult.data.data.data : [];
  const pagination = isSuccess && ticketsResult.data ? ticketsResult.data.data.pagination : null;
  const totalPages = pagination?.total_pages ?? 1;
  const totalCount = pagination?.total_count ?? 0;

  // ── Filter modal handlers ────────────────────────
  const handleOpenFilterModal = () => {
    setDraftFilters({ ...appliedFilters });
    setIsFilterModalOpen(true);
  };

  const handleApplyFilters = () => {
    setAppliedFilters({ ...draftFilters });
    setPage(1);
    setIsFilterModalOpen(false);
  };

  // ── Card toggle handler ──────────────────────────
  const handleToggle = (ticketId: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(ticketId)) {
        next.delete(ticketId);
      } else {
        next.add(ticketId);
      }
      return next;
    });
  };

  const hasActiveFilters =
    appliedFilters.scaleTicketId !== "" ||
    appliedFilters.shipDate !== "" ||
    appliedFilters.plcId !== "" ||
    appliedFilters.commodityId !== "" ||
    appliedFilters.customerIds.length > 0 ||
    (!!appliedFilters.shipFrom) ||
    (!!appliedFilters.shipTo);

  return (
    <div className="flex flex-col gap-4 h-full overflow-hidden">
      {/* Filter Button */}
      <div className="flex items-center gap-2">
        <Button variant="outline" size="sm" onClick={handleOpenFilterModal}>
          <SlidersHorizontal className="h-4 w-4 mr-2" />
          Filters
          {hasActiveFilters && (
            <span className="ml-2 rounded-full bg-primary text-primary-foreground px-2 py-0.5 text-xs">
              Active
            </span>
          )}
        </Button>
        {ticketsResult.isFetching && (
          <span className="ml-2 text-sm text-muted-foreground">Loading...</span>
        )}
      </div>

      {/* Filter Modal */}
      <TicketFilterModal
        open={isFilterModalOpen}
        onOpenChange={setIsFilterModalOpen}
        draftFilters={draftFilters}
        setDraftFilters={setDraftFilters}
        onApply={handleApplyFilters}
        locationOptions={locationOptions}
        customerOptions={customerOptions}
        shipCustomerOptions={shipCustomerOptions}
        commodityOptions={commodityOptions}
      />

      {/* Contract Tickets Modal */}
      <ContractTicketsModal
        contractNbr={selectedContractInfo?.contract_nbr ?? null}
        contractId={selectedContractInfo?.contract_id ?? null}
        settlementNbr={selectedContractInfo?.settlement_nbr ?? null}
        advanceNbr={selectedContractInfo?.advance_nbr ?? null}
        onClose={() => setSelectedContractInfo(null)}
      />

      {/* Card List */}
      <div className="flex-1 overflow-y-auto space-y-2">
        {ticketsResult.isLoading && (
          <div className="text-sm text-muted-foreground text-center py-8">Loading...</div>
        )}
        {!ticketsResult.isLoading && tickets.length === 0 && (
          <div className="text-sm text-muted-foreground text-center py-8">No tickets found.</div>
        )}
        {!ticketsResult.isLoading &&
          tickets.map((ticket) => (
            <TicketCard
              key={ticket.scale_ticket_id}
              ticket={ticket}
              isExpanded={expandedIds.has(ticket.scale_ticket_id)}
              onToggle={() => handleToggle(ticket.scale_ticket_id)}
              onContractClick={setSelectedContractInfo}
            />
          ))}
      </div>

      {/* Pagination Controls */}
      {!ticketsResult.isLoading && (
        <div className="flex items-center justify-between py-2">
          <div className="text-sm text-muted-foreground">
            Page {page} of {totalPages} | Total: {totalCount} tickets
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1}
            >
              <ChevronLeft className="h-4 w-4 mr-1" />
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages}
            >
              Next
              <ChevronRight className="h-4 w-4 ml-1" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
